import { api, ApiError, type Location } from "../api";
import * as clientDb from "../db/clientDb";
import * as mutations from "../sync/mutations";
import { performSync } from "../sync/syncEngine";
import { createPhotoPicker } from "../components/photoPicker";
import { createSwipeRow } from "../components/swipeRow";
import { showToast, showUndoToast } from "../components/toast";
import { msg } from "../messages";
import { isNavigationStale } from "../navigation";
import { closeAnimatedSheet, mountAnimatedSheet } from "../utils/sheetAnimation";
import { resolveCachedImageSrc } from "../utils/images";
import { createIcon } from "../utils/icons";
import { setSheetError } from "../utils/sheetFormError";

let zonesCache: Location[] = [];
let isZonesAnimating = 0;

function waitAnimation(el: HTMLElement, fallbackMs: number): Promise<void> {
    return new Promise((resolve) => {
        let settled = false;
        const finish = () => {
            if (settled) return;
            settled = true;
            el.removeEventListener("animationend", onEnd);
            resolve();
        };
        const onEnd = (e: AnimationEvent) => {
            if (e.target === el) finish();
        };
        el.addEventListener("animationend", onEnd);
        window.setTimeout(finish, fallbackMs);
    });
}

function formatZoneStatus(count: number): string {
    return count === 1 ? "1 ZONE" : `${count} ZONES`;
}

function renderZonesLoadingShell(container: HTMLElement): void {
    container.innerHTML = "";

    const page = document.createElement("div");
    page.className = "zones-page zones-page--loading view-enter";

    const masthead = document.createElement("div");
    masthead.className = "zones-masthead";

    const subBar = document.createElement("div");
    subBar.className = "zones-shimmer-sub-bar loading-shimmer cascade-glide";
    subBar.style.setProperty("--stagger", "0");
    masthead.appendChild(subBar);

    const rows = document.createElement("div");
    for (let i = 0; i < 5; i++) {
        const row = document.createElement("div");
        row.className = "zones-shimmer-row loading-shimmer cascade-glide";
        row.style.setProperty("--stagger", String(1 + Math.min(i, 4)));
        rows.appendChild(row);
    }

    page.appendChild(masthead);
    page.appendChild(rows);
    container.appendChild(page);
}

async function transitionToZonesContent(
    container: HTMLElement,
    locations: Location[],
    gen: number,
): Promise<void> {
    const loadingPage = container.querySelector<HTMLElement>(".zones-page--loading");
    if (loadingPage) {
        loadingPage.classList.add("zones-page--exiting");
        await waitAnimation(loadingPage, 340);
    }
    if (isNavigationStale(gen)) return;
    renderZonesContent(container, locations, { animate: true });
}

export async function renderLocations(container: HTMLElement, gen: number): Promise<void> {
    if (isNavigationStale(gen)) return;
    renderZonesLoadingShell(container);

    window.addEventListener("pantry-sync", () => {
        if (isNavigationStale(gen) || isZonesAnimating > 0) return;
        void refreshZones(container);
    });

    window.addEventListener("pantry-id-remap", (e: Event) => {
        const { table, tempId, serverId } = (e as CustomEvent).detail;
        if (table !== "locations") return;
        const row = container.querySelector<HTMLElement>(`.swipe-row[data-zone-id="${tempId}"]`);
        if (row) row.dataset.zoneId = String(serverId);
        const loc = zonesCache.find((l) => l.id === tempId);
        if (loc) loc.id = serverId;
    });

    try {
        const locations = await api.locations.list();
        if (isNavigationStale(gen)) return;
        await transitionToZonesContent(container, locations, gen);
    } catch {
        if (isNavigationStale(gen)) return;
        container.innerHTML = `
      <div class="empty-state zones-empty cascade-glide">
        <p>${msg("locations/load-error")}</p>
      </div>
    `;
    }
}

function renderZonesContent(
    container: HTMLElement,
    locations: Location[],
    { animate }: { animate: boolean },
): void {
    zonesCache = locations;
    container.innerHTML = "";

    const page = document.createElement("div");
    page.className = "zones-page";
    if (animate) page.classList.add("view-enter");

    const masthead = document.createElement("div");
    masthead.className = `zones-masthead${animate ? " cascade-fade" : ""}`;

    const subBar = document.createElement("div");
    subBar.className = "zones-sub-bar";

    const status = document.createElement("span");
    status.className = "zones-status";
    status.textContent = formatZoneStatus(locations.length);

    const addBtn = document.createElement("button");
    addBtn.type = "button";
    addBtn.className = "zones-add-btn";
    addBtn.setAttribute("aria-label", "Add zone");
    addBtn.textContent = "+";
    addBtn.addEventListener("click", () => openLocationForm(container));

    subBar.appendChild(status);
    subBar.appendChild(addBtn);
    masthead.appendChild(subBar);
    page.appendChild(masthead);

    const section = document.createElement("section");
    section.className = `zones-section${animate ? " cascade-glide" : ""}`;
    if (animate) section.style.setProperty("--stagger", "1");

    mountZonesList(section, container, locations, animate);
    page.appendChild(section);
    container.appendChild(page);
}

function mountZonesList(
    section: HTMLElement,
    container: HTMLElement,
    locations: Location[],
    animate: boolean,
): void {
    section.replaceChildren();

    if (locations.length === 0) {
        const empty = document.createElement("div");
        empty.className = "empty-state zones-empty";

        const message = document.createElement("p");
        message.textContent = msg("locations/empty");

        const actions = document.createElement("div");
        actions.className = "empty-actions";

        const addBtn = document.createElement("button");
        addBtn.type = "button";
        addBtn.className = "btn btn-primary btn-block";
        addBtn.setAttribute("aria-label", "Add zone");
        addBtn.append(createIcon("add", "icon-sm"), " Add zone");
        addBtn.addEventListener("click", () => openLocationForm(container));

        actions.appendChild(addBtn);
        empty.appendChild(message);
        empty.appendChild(actions);
        section.appendChild(empty);
        return;
    }

    const list = document.createElement("div");
    list.className = "zones-list";

    for (let i = 0; i < locations.length; i++) {
        const row = renderZoneRow(i, container);
        if (animate) {
            row.classList.add("cascade-glide");
            row.style.setProperty("--stagger", String(2 + Math.min(i, 10)));
        }
        list.appendChild(row);
    }

    section.appendChild(list);
}

function zonesMatch(a: Location[], b: Location[]): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (String(a[i].id) !== String(b[i].id)) return false;
    }
    return true;
}

function isZoneReorder(a: Location[], b: Location[]): boolean {
    if (a.length !== b.length) return false;
    const aIds = new Set(a.map((l) => String(l.id)));
    return b.every((l) => aIds.has(String(l.id)));
}

function updateZonesList(container: HTMLElement, locations: Location[]): void {
    const status = container.querySelector(".zones-status");
    if (status) status.textContent = formatZoneStatus(locations.length);

    if (zonesMatch(zonesCache, locations)) return;

    const section = container.querySelector(".zones-section");
    if (!section) {
        zonesCache = locations;
        renderZonesContent(container, locations, { animate: false });
        return;
    }

    if (isZoneReorder(zonesCache, locations) && locations.length > 0) {
        reorderZonesList(container, section as HTMLElement, locations);
    } else {
        zonesCache = locations;
        mountZonesList(section as HTMLElement, container, locations, false);
    }
}

function reorderZonesList(container: HTMLElement, section: HTMLElement, locations: Location[]): void {
    const list = section.querySelector(".zones-list") as HTMLElement | null;
    if (!list) {
        zonesCache = locations;
        mountZonesList(section, container, locations, false);
        return;
    }

    const oldRows = [...list.querySelectorAll<HTMLElement>(".swipe-row")];
    const oldTops = new Map<number, number>();
    const rowById = new Map<number, HTMLElement>();
    for (const row of oldRows) {
        const id = Number(row.dataset.zoneId);
        if (!Number.isFinite(id)) continue;
        oldTops.set(id, row.getBoundingClientRect().top);
        rowById.set(id, row);
    }

    isZonesAnimating++;
    zonesCache = locations;

    const newRowIds = new Set<number>();
    const newRows: HTMLElement[] = [];
    for (let i = 0; i < locations.length; i++) {
        const id = Number(locations[i].id);
        if (!Number.isFinite(id)) continue;
        newRowIds.add(id);
        const row = rowById.get(id);
        if (row) {
            list.appendChild(row);
            newRows.push(row);
        } else {
            const newRow = renderZoneRow(i, container);
            list.appendChild(newRow);
            newRows.push(newRow);
        }
    }

    for (const row of oldRows) {
        const id = Number(row.dataset.zoneId);
        if (!newRowIds.has(id)) row.remove();
    }

    void list.offsetHeight;

    const animations: Animation[] = [];
    for (const row of newRows) {
        const id = Number(row.dataset.zoneId);
        if (!Number.isFinite(id)) continue;
        const oldTop = oldTops.get(id);
        if (oldTop === undefined) continue;
        const newTop = row.getBoundingClientRect().top;
        const delta = oldTop - newTop;
        if (Math.abs(delta) < 1) continue;

        row.getAnimations().forEach((anim) => anim.cancel());
        row.classList.remove("cascade-glide");
        row.style.willChange = "transform";
        const anim = row.animate(
            [{ transform: `translateY(${delta}px)` }, { transform: "translateY(0)" }],
            { duration: ZONE_SWAP_MS, easing: ZONE_EASE, fill: "forwards" },
        );
        anim.finished.catch(() => undefined);
        animations.push(anim);
    }

    syncZoneRowButtons(list);

    window.setTimeout(() => {
        for (const row of newRows) {
            row.classList.remove("cascade-glide");
            row.style.removeProperty("will-change");
        }
        for (const anim of animations) {
            anim.cancel();
        }
        isZonesAnimating--;
    }, ZONE_SWAP_MS + 40);
}

function renderZoneRow(index: number, container: HTMLElement): HTMLElement {
    const loc = zonesCache[index];
    const content = document.createElement("div");
    content.className = "zone-row";

    if (loc.image_path) {
        const img = document.createElement("img");
        img.alt = "";
        img.className = "zone-thumb";
        void resolveCachedImageSrc(loc.image_path).then((src) => {
            if (src) img.src = src;
        });
        content.appendChild(img);
    } else {
        const placeholder = document.createElement("div");
        placeholder.className = "zone-thumb zone-thumb--placeholder";
        placeholder.appendChild(createIcon("place", "icon-lg"));
        content.appendChild(placeholder);
    }

    const info = document.createElement("div");
    info.className = "zone-info";
    const name = document.createElement("span");
    name.className = "zone-name";
    name.textContent = loc.name;
    info.appendChild(name);
    content.appendChild(info);

    const actions = document.createElement("div");
    actions.className = "zone-actions";

    const upBtn = document.createElement("button");
    upBtn.type = "button";
    upBtn.className = "zone-action-btn";
    upBtn.setAttribute("aria-label", "Move up");
    upBtn.appendChild(createIcon("arrow_upward", "icon-sm"));
    upBtn.disabled = index === 0;

    const downBtn = document.createElement("button");
    downBtn.type = "button";
    downBtn.className = "zone-action-btn";
    downBtn.setAttribute("aria-label", "Move down");
    downBtn.appendChild(createIcon("arrow_downward", "icon-sm"));
    downBtn.disabled = index === zonesCache.length - 1;

    let reordering = false;
    function handleReorder(direction: -1 | 1, e: Event): void {
        e.stopPropagation();
        if (reordering || isZonesAnimating > 0) return;
        const id = Number(swipeRow.dataset.zoneId);
        if (!Number.isFinite(id)) return;

        const from = findZoneIndex(id);
        const to = from + direction;
        if (from < 0 || to < 0 || to >= zonesCache.length) return;

        reordering = true;
        isZonesAnimating++;
        void swapOrder(id, direction, container).finally(() => {
            // The reorder guard must cover the CSS swap animation plus cleanup
            // so delayed click/pointerup events or rapid taps don't start an
            // overlapping swap and snap the row back.
            window.setTimeout(() => {
                reordering = false;
                isZonesAnimating--;
            }, ZONE_SWAP_MS + 80);
        });
    }

    upBtn.addEventListener("pointerup", (e) => handleReorder(-1, e));
    upBtn.addEventListener("click", (e) => handleReorder(-1, e));
    downBtn.addEventListener("pointerup", (e) => handleReorder(1, e));
    downBtn.addEventListener("click", (e) => handleReorder(1, e));

    actions.appendChild(upBtn);
    actions.appendChild(downBtn);
    content.appendChild(actions);

    const swipeRow = createSwipeRow(content, {
        onEdit: () => openLocationForm(container, loc),
    });
    swipeRow.dataset.zoneId = String(loc.id);
    return swipeRow;
}

async function refreshZones(container: HTMLElement): Promise<void> {
    if (isZonesAnimating > 0) return;
    try {
        const locations = await api.locations.list();
        if (isZonesAnimating > 0) return;
        updateZonesList(container, locations);
    } catch {
        /* silent failure */
    }
}

function findZoneIndex(locationId: number): number {
    return zonesCache.findIndex((z) => Number(z.id) === locationId);
}

function syncZoneRowButtons(list: HTMLElement): void {
    const rows = [...list.querySelectorAll<HTMLElement>(".swipe-row")];
    rows.forEach((row, i) => {
        const up = row.querySelector<HTMLButtonElement>('[aria-label="Move up"]');
        const down = row.querySelector<HTMLButtonElement>('[aria-label="Move down"]');
        if (up) up.disabled = i === 0;
        if (down) down.disabled = i === rows.length - 1;
    });
}

const ZONE_SWAP_MS = 480;

const ZONE_EASE = "cubic-bezier(0.25, 1, 0.5, 1)";

function animateZoneSwap(list: HTMLElement, from: number, to: number): void {
    const rows = [...list.querySelectorAll<HTMLElement>(".swipe-row")];
    const rowA = rows[from];
    const rowB = rows[to];
    if (!rowA || !rowB || from === to) return;

    const firstA = rowA.getBoundingClientRect();
    const firstB = rowB.getBoundingClientRect();

    if (from < to) {
        list.insertBefore(rowA, rowB.nextSibling);
    } else {
        list.insertBefore(rowA, rowB);
    }

    void list.offsetHeight;

    const deltaA = firstA.top - rowA.getBoundingClientRect().top;
    const deltaB = firstB.top - rowB.getBoundingClientRect().top;

    const movers = [
        { row: rowA, delta: deltaA, active: true },
        { row: rowB, delta: deltaB, active: false },
    ];
    const animations: Animation[] = [];

    for (const { row, delta, active } of movers) {
        if (Math.abs(delta) < 1) continue;

        // Cancel any running animations so the new WAAPI transform animation can run.
        row.getAnimations().forEach((anim) => anim.cancel());
        row.classList.remove(
            "cascade-glide",
            "zone-row--reordering",
            "zone-row--swap-active",
            "zone-row--swap-partner",
        );
        row.classList.add("zone-row--reordering");
        row.classList.add(active ? "zone-row--swap-active" : "zone-row--swap-partner");
        row.style.willChange = "transform";
        const anim = row.animate(
            [{ transform: `translateY(${delta}px)` }, { transform: "translateY(0)" }],
            { duration: ZONE_SWAP_MS, easing: ZONE_EASE, fill: "forwards" },
        );
        anim.finished.catch(() => undefined);
        animations.push(anim);
    }

    window.setTimeout(() => {
        for (const { row } of movers) {
            row.classList.remove(
                "zone-row--reordering",
                "zone-row--swap-active",
                "zone-row--swap-partner",
            );
            row.style.removeProperty("will-change");
        }
        for (const anim of animations) {
            anim.cancel();
        }
    }, ZONE_SWAP_MS + 40);
}

async function swapOrder(
    locationId: number,
    direction: -1 | 1,
    container: HTMLElement,
): Promise<void> {
    const list = container.querySelector(".zones-list");
    if (!list) return;

    const from = findZoneIndex(locationId);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= zonesCache.length) return;

    const a = zonesCache[from];
    const b = zonesCache[to];
    const aOrder = Number(a.sort_order);
    const bOrder = Number(b.sort_order);

    const next = [...zonesCache];
    if (aOrder === bOrder) {
        const min = Math.min(from, to);
        const max = Math.max(from, to);
        next[min] = { ...next[min], sort_order: aOrder };
        next[max] = { ...next[max], sort_order: aOrder + 1 };
    } else {
        next[from] = { ...b, sort_order: aOrder };
        next[to] = { ...a, sort_order: bOrder };
    }
    zonesCache = next;

    animateZoneSwap(list as HTMLElement, from, to);
    syncZoneRowButtons(list as HTMLElement);

    try {
        await mutations.updateLocation(Number(a.id), { sort_order: next[to].sort_order });
        await mutations.updateLocation(Number(b.id), { sort_order: next[from].sort_order });
        void performSync();
    } catch (err) {
        try {
            const locations = await api.locations.list();
            updateZonesList(container, locations);
        } catch {
            const reverted = [...zonesCache];
            reverted[from] = { ...a };
            reverted[to] = { ...b };
            zonesCache = reverted;
            animateZoneSwap(list as HTMLElement, to, from);
            syncZoneRowButtons(list as HTMLElement);
        }
    }
}

function openLocationForm(container: HTMLElement, existing?: Location): void {
    const overlay = document.createElement("div");
    overlay.className = "sheet-overlay";

    const sheet = document.createElement("div");
    sheet.className = "item-sheet";

    const header = document.createElement("header");
    header.className = "item-sheet-header";

    const title = document.createElement("h2");
    title.className = "item-sheet-title";
    title.textContent = existing ? `Edit: ${existing.name}` : "Add Zone";

    const dismissBtn = document.createElement("button");
    dismissBtn.type = "button";
    dismissBtn.className = "item-sheet-dismiss";
    dismissBtn.setAttribute("aria-label", "Close");
    dismissBtn.textContent = "×";

    header.appendChild(title);
    header.appendChild(dismissBtn);

    const body = document.createElement("div");
    body.className = "item-sheet-body";

    const form = document.createElement("form");
    form.className = "item-sheet-form";
    form.innerHTML = `
    <div class="ledger-field">
      <label class="ledger-label" for="loc-name">Name</label>
      <input id="loc-name" class="ledger-input" type="text" required value="${existing ? escapeAttr(existing.name) : ""}" autocomplete="off">
    </div>
    <div class="ledger-field">
      <span class="ledger-label">Photo</span>
      <div id="loc-photo-picker"></div>
    </div>
    <button type="submit" class="item-sheet-submit">${existing ? "UPDATE ZONE" : "ADD ZONE"}</button>
    ${existing ? '<button type="button" id="loc-delete" class="item-sheet-danger">Delete zone</button>' : ""}
  `;

    body.appendChild(form);
    sheet.appendChild(header);
    sheet.appendChild(body);
    overlay.appendChild(sheet);

    let closed = false;
    function close(): void {
        if (closed) return;
        closed = true;
        closeAnimatedSheet(overlay);
    }

    dismissBtn.addEventListener("click", close);
    overlay.addEventListener("click", (e) => {
        if (e.target === overlay) close();
    });

    mountAnimatedSheet(overlay);

    const photoPicker = createPhotoPicker({ existingImageSrc: existing?.image_path ?? null });
    form.querySelector("#loc-photo-picker")!.appendChild(photoPicker.element);

    if (existing && !existing.image_path) {
        void clientDb.getPendingImageForRecord("locations", existing.id).then((pending) => {
            if (pending) {
                const blobUrl = URL.createObjectURL(pending.file);
                const img = photoPicker.element.querySelector<HTMLImageElement>(".photo-attach-img");
                if (img) {
                    img.src = blobUrl;
                    img.classList.remove("hidden");
                    photoPicker.element.querySelector(".photo-attach-placeholder")?.classList.add("hidden");
                    photoPicker.element.querySelector(".photo-attach-trigger")?.classList.add("has-image");
                    photoPicker.element.querySelector(".photo-attach-action--danger")?.classList.remove("hidden");
                }
            }
        });
    }

    form.addEventListener("submit", async (e) => {
        e.preventDefault();
        setSheetError(form, null);

        const name = (form.querySelector("#loc-name") as HTMLInputElement).value.trim();
        if (!name) {
            setSheetError(form, msg("toast/enter-name"));
            return;
        }

        const submitBtn = form.querySelector(".item-sheet-submit") as HTMLButtonElement;
        submitBtn.disabled = true;
        const prevText = submitBtn.textContent;
        submitBtn.textContent = existing ? "Updating…" : "Adding…";

        let image_path: string | null = existing?.image_path ?? null;
        let pendingImage: Blob | undefined;
        const file = photoPicker.getFile();
        const cleared = photoPicker.getCleared();
        if (cleared) {
            image_path = null;
        } else if (file) {
            try {
                const result = await api.upload.location(file);
                image_path = result.path;
            } catch {
                pendingImage = file;
            }
        }

        try {
            if (existing) {
                await api.locations.update(existing.id, { name, image_path }, pendingImage);
                showToast(msg("toast/zone-updated"));
            } else {
                await api.locations.create({ name, image_path }, pendingImage);
                showToast(msg("toast/zone-created"));
            }
            close();
            await refreshZones(container);
        } catch (err) {
            submitBtn.disabled = false;
            submitBtn.textContent = prevText ?? "";
            setSheetError(form, err instanceof ApiError ? err.message : msg("toast/failed-save"));
        }
    });

    form.querySelector("#loc-delete")?.addEventListener("click", () => {
        if (!existing) return;
        void attemptZoneDeletion(existing, container, close);
    });
}

async function attemptZoneDeletion(
    loc: Location,
    container: HTMLElement,
    closeSheet: () => void,
): Promise<void> {
    try {
        const items = await api.items.list({ location: loc.id });
        if (items.length > 0) {
            closeSheet();
            showToast(msg("toast/cannot-delete-zone"));
            return;
        }
    } catch {
        return;
    }

    closeSheet();
    beginDeferredLocationRemoval(loc, container);
}

function beginDeferredLocationRemoval(loc: Location, container: HTMLElement): void {
    const remaining = zonesCache.filter((l) => l.id !== loc.id);
    updateZonesList(container, remaining);

    showUndoToast({
        message: msg("toast/removed", { name: loc.name }),
        durationMs: 5000,
        onUndo: () => {
            const restored = [...remaining, loc].sort((a, b) => a.sort_order - b.sort_order);
            updateZonesList(container, restored);
        },
        onComplete: async () => {
            try {
                await api.locations.delete(loc.id);
            } catch {
                const restored = [...remaining, loc].sort((a, b) => a.sort_order - b.sort_order);
                updateZonesList(container, restored);
                showToast(msg("toast/cannot-delete-zone"));
            }
        },
    });
}

function escapeAttr(text: string): string {
    return text.replace(/"/g, "&quot;").replace(/</g, "&lt;");
}
